import type { SuperAdminDashboardSummary } from "@multi-chat/shared";
import { Building2, CirclePause, CircleCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { cn } from "../../lib/utils.js";

export type SuperAdminLoadState<T> =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; data: T };

type MetricTone = "accent" | "success" | "warning";

const TONE_CHIP: Record<MetricTone, string> = {
  accent: "border-accent/20 bg-accent/10 text-accent",
  success: "border-success/20 bg-success-muted text-success",
  warning: "border-warning/25 bg-warning-muted text-warning",
};

function CountCell({
  label,
  hint,
  value,
  testId,
  tone,
  icon: Icon,
}: {
  label: string;
  hint: string;
  value: number;
  testId: string;
  tone: MetricTone;
  icon: typeof Building2;
}): React.JSX.Element {
  return (
    <section className="flex flex-col rounded-xl border border-line bg-surface px-5 py-5" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="m-0 text-[0.8125rem] font-semibold text-foreground">{label}</h2>
        <span className={cn("inline-grid size-8 shrink-0 place-items-center rounded-lg border", TONE_CHIP[tone])}>
          <Icon size={16} aria-hidden="true" />
        </span>
      </div>
      <p className="m-0 mt-5 text-[2.25rem] font-semibold leading-none tracking-[-0.03em] text-foreground tabular-nums">
        {value}
      </p>
      <p className="m-0 mt-3 border-t border-line pt-3 text-[0.75rem] text-muted">{hint}</p>
    </section>
  );
}

export function SuperAdminDashboardView({
  state,
  onRetry,
}: {
  state: SuperAdminLoadState<SuperAdminDashboardSummary>;
  onRetry: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (state.status === "loading") {
    return (
      <div className="grid gap-5" data-testid="super-admin-dashboard-loading" aria-busy="true">
        <div className="grid gap-2">
          <div className="h-6 w-36 animate-pulse rounded-md bg-surface-muted" />
          <div className="h-4 w-56 animate-pulse rounded-md bg-surface-muted" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className="h-36 animate-pulse rounded-xl border border-line bg-surface" />
          ))}
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <ProductErrorState
        testId="super-admin-dashboard-error"
        title={t("superAdmin.dashboard.loadErrorTitle")}
        description={t("superAdmin.dashboard.loadErrorBody")}
        onRetry={onRetry}
        retryLabel={t("superAdmin.retry")}
      />
    );
  }

  return (
    <div className="grid gap-5" data-testid="super-admin-dashboard">
      <header className="grid gap-1">
        <h1 className="m-0 text-xl font-semibold text-foreground">{t("superAdmin.dashboard.title")}</h1>
        <p className="m-0 text-sm text-muted">{t("superAdmin.dashboard.lead")}</p>
      </header>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <CountCell
          label={t("superAdmin.dashboard.total")}
          hint={t("superAdmin.dashboard.totalHint")}
          value={state.data.total}
          testId="super-admin-count-total"
          tone="accent"
          icon={Building2}
        />
        <CountCell
          label={t("superAdmin.dashboard.active")}
          hint={t("superAdmin.dashboard.activeHint")}
          value={state.data.active}
          testId="super-admin-count-active"
          tone="success"
          icon={CircleCheck}
        />
        <CountCell
          label={t("superAdmin.dashboard.suspended")}
          hint={t("superAdmin.dashboard.suspendedHint")}
          value={state.data.suspended}
          testId="super-admin-count-suspended"
          tone="warning"
          icon={CirclePause}
        />
      </div>
    </div>
  );
}
